// Updated InvoiceDocument.jsx
import React from 'react';
import { Page, Text, View, Document, StyleSheet, Image } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: {
    padding: 10,
    fontSize: 8,
    lineHeight: 1.2,
    fontFamily: 'Helvetica'
  },
  section: { marginBottom: 6 },
  row: { flexDirection: 'row' },
  col: { flexGrow: 1 },
  border: { border: '0.5px solid black' },
  bold: { fontWeight: 'bold' },
  center: { textAlign: 'center' },
  right: { textAlign: 'right' },
  underline: { textDecoration: 'underline' }
});

export default function InvoiceDocument({ invoice, items, settings, logo, customer, qrCodeDataUrl, showShipTo }) {
  const totalCgst = items.reduce((sum, i) => sum + Number(i.CGST || 0), 0);
  const totalSgst = items.reduce((sum, i) => sum + Number(i.SGST || 0), 0);
  const subTotal = items.reduce((sum, i) => sum + Number(i.Amount || (i.Quantity * i.Rate)), 0);
  const netTotal = subTotal + totalCgst + totalSgst;

  const cgstPercArr = items.map(i => Number(i.GSTPercent || 0) / 2).filter(p => p > 0);
  const cgstPerc = cgstPercArr.length ? [...new Set(cgstPercArr)].join(', ') : '0';

  const numberToWords = (num) => {
    const a = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
      'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    if ((num = num.toString()).length > 9) return 'Amount too large';
    const n = ('000000000' + num).substr(-9).match(/^\d{2}(\d{2})(\d{2})(\d{1})(\d{2})$/);
    if (!n) return '';
    let str = '';
    str += (n[1] !== '00') ? (a[Number(n[1])] || (b[n[1][0]] + ' ' + a[n[1][1]])) + ' Lakh ' : '';
    str += (n[2] !== '00') ? (a[Number(n[2])] || (b[n[2][0]] + ' ' + a[n[2][1]])) + ' Thousand ' : '';
    str += (n[3] !== '0')  ? (a[Number(n[3])] || (b[n[3][0]] + ' ' + a[n[3][1]])) + ' Hundred ' : '';
    str += (n[4] !== '00') ? ((str ? 'and ' : '') + (a[Number(n[4])] || (b[n[4][0]] + ' ' + a[n[4][1]]))) + ' ' : '';
    return str + 'Rupees';
  };

  return (
    <Document>
      <Page size={{ width: 348, height: 496 }} style={styles.page}>
        {/* Header */}
        <View style={[styles.section, styles.row]}>
          {logo && <Image src={`${logo?.FilePath}`} style={{ height: 30, width: 30 }} />}
          <View style={[styles.col, { paddingLeft: 6 }]}>
            <Text style={styles.bold}>{settings.BusinessName}</Text>
            <Text>{settings.Address}</Text>
            <Text>GSTIN: {settings.GSTIN}</Text>
          </View>
          <View style={[styles.col, styles.right]}>
            {settings.Phone?.split(',').map((ph, i) => (
              <Text key={i}>Ph: {ph.trim()}</Text>
            ))}
          </View>
        </View>

        <Text style={[styles.center, styles.underline, styles.section]}>TAX INVOICE</Text>

        {/* Customer + Invoice Info */}
        <View style={[styles.row, styles.section]}>
          <View style={[styles.border, { flex: 1, padding: 2 }]}>
            <Text style={styles.bold}>BILL TO</Text>
            <Text>{customer.name}</Text>
            <Text>{customer.address}</Text>
            <Text>Mobile: {customer.phone}</Text>
            <Text>GSTIN: {customer.gstin}</Text>
          </View>
          {showShipTo && (
            <View style={[styles.border, { flex: 1, padding: 2 }]}>
              <Text style={styles.bold}>SHIP TO</Text>
              <Text>{customer.name}</Text>
              <Text>{customer.address}</Text>
              <Text>Mobile: {customer.phone}</Text>
              <Text>GSTIN: {customer.gstin}</Text>
            </View>
          )}
          <View style={[styles.border, { flex: 1, padding: 2 }]}>
            <Text>Invoice No: {invoice.InvoiceNo}</Text>
            <Text>Invoice Date: {invoice.InvoiceDate}</Text>
          </View>
        </View>

        {/* Items Table */}
        <View style={[styles.border, styles.section]}>
          <View style={[styles.row, styles.border]}>
            <Text style={[styles.col, styles.border]}>No</Text>
            <Text style={[styles.col, styles.border]}>Description</Text>
            <Text style={[styles.col, styles.border]}>HSN</Text>
            <Text style={[styles.col, styles.border]}>Size</Text>
            <Text style={[styles.col, styles.border]}>Qty</Text>
            <Text style={[styles.col, styles.border]}>Rate</Text>
            <Text style={[styles.col, styles.border]}>Amount</Text>
          </View>
          {items.map((item, idx) => (
            <View style={styles.row} key={idx}>
              <Text style={[styles.col, styles.border]}>{idx + 1}</Text>
              <Text style={[styles.col, styles.border]}>{item.Description}</Text>
              <Text style={[styles.col, styles.border]}>{item.HSN}</Text>
              <Text style={[styles.col, styles.border]}>{item.Size}</Text>
              <Text style={[styles.col, styles.border]}>{item.Quantity}</Text>
              <Text style={[styles.col, styles.border]}>{item.Rate}</Text>
              <Text style={[styles.col, styles.border]}>{item.Amount}</Text>
            </View>
          ))}
        </View>

        {/* Tax Rows */}
        <View style={styles.section}>
          <Text>CGST @{cgstPerc}%: ₹{totalCgst.toFixed(2)}</Text>
          <Text>SGST @{cgstPerc}%: ₹{totalSgst.toFixed(2)}</Text>
          <Text style={styles.bold}>Total: ₹{netTotal.toFixed(2)}</Text>
        </View>

        {/* Amount in Words */}
        <Text style={styles.section}><Text style={styles.bold}>Total (in words): </Text>{numberToWords(netTotal.toFixed(0))}</Text>

        {/* QR + Footer */}
        <View style={[styles.row, styles.border, { padding: 4 }]}>
          <View style={[styles.col]}>
            <Text style={styles.bold}>Bank</Text>
            <Text>{settings.BusinessName}</Text>
            <Text>A/C: {settings.AccountNumber}</Text>
            <Text>IFSC: {settings.IFSCCode}</Text>
          </View>
          <View style={[styles.col, styles.center]}>
            <Text style={styles.bold}>UPI QR</Text>
            {qrCodeDataUrl && <Image src={qrCodeDataUrl} style={{ height: 60, width: 60, marginVertical: 2 }} />}
            <Text>{settings?.UPIId}</Text>
          </View>
          <View style={[styles.col, styles.center]}>
            <Text>Thank You</Text>
            <Text>Visit Again</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
}
